import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { LngLatLike } from 'mapbox-gl';
import moment from 'moment';
import { FlightWatchModelResponse } from 'src/app/models/flight-watch';

// Services
import { SharedService } from '../../../layouts/shared-service';
import * as SharedEvents from '../../../constants/sharedEvents';
import { StatisticsOrdersByLocationComponent } from '../../../shared/components/statistics-orders-by-location/statistics-orders-by-location.component';
import { StatisticsTotalAircraftComponent } from '../../../shared/components/statistics-total-aircraft/statistics-total-aircraft.component';
import { StatisticsTotalCustomersComponent } from '../../../shared/components/statistics-total-customers/statistics-total-customers.component';
// Components
import { StatisticsTotalOrdersComponent } from '../../../shared/components/statistics-total-orders/statistics-total-orders.component';
import { FlightWatchMapService } from '../../flight-watch/flight-watch-map/flight-watch-map-services/flight-watch-map.service';
import {localStorageAccessConstant } from 'src/app/constants/LocalStorageAccessConstant';
import { StorageService } from 'src/app/services/security/storage.service';
import { Subscription } from 'rxjs';
import { MapQueryParams, MapType } from 'src/app/enums/map.enum';

export interface DashboardWidget {
    id: string;
    label: string;
    colClass: string;
    hiddenClass?: string;
}

export const DEFAULT_WIDGET_ORDER: DashboardWidget[] = [
    { id: 'pricing-update', label: 'Pricing Update Generator', colClass: 'col-12' },
    { id: 'lost-opportunities', label: 'Lost Opportunities', colClass: 'col-sm-12 col-md-6 col-lg-6 col-xl-6' },
    { id: 'new-fuelerlinx-operators', label: 'New FuelerLinx Operators', colClass: 'col-sm-12 col-md-6 col-lg-6 col-xl-6' },
    { id: 'flight-watch', label: 'Flight Watch', colClass: 'col-sm-12 col-md-12 col-lg-12 col-xl-12', hiddenClass: 'd-none d-md-block' },
    { id: 'lost-to-competition', label: 'Lost To Competition', colClass: 'col-sm-12 col-md-12 col-lg-12 col-xl-12' },
    { id: 'total-customers', label: 'Total Customers', colClass: 'col-md-6 col-xl-3' },
    { id: 'total-aircraft', label: 'Total Aircraft', colClass: 'col-md-6 col-xl-3' },
    { id: 'total-orders', label: 'Total Orders', colClass: 'col-md-6 col-xl-3' },
    { id: 'orders-by-location', label: 'Orders By Location', colClass: 'col-md-6 col-xl-3' },
];

@Component({
    selector: 'app-dashboard-fbo-updated',
    styleUrls: ['./dashboard-fbo-updated.component.scss'],
    templateUrl: './dashboard-fbo-updated.component.html',
})
export class DashboardFboUpdatedComponent implements OnInit, AfterViewInit, OnDestroy {
    public pageTitle = 'Dashboard';
    public fboid: any;
    public groupid: any;
    public updatedPrice: any;
    public locationChangedSubscription: any;
    public filterStartDate: Date;
    public filterEndDate: Date;
    public pastThirtyDaysStartDate: Date;
    @ViewChildren('statisticsTotalOrders')
    private statisticsTotalOrdersList: QueryList<StatisticsTotalOrdersComponent>;
    @ViewChildren('statisticsTotalCustomers')
    private statisticsTotalCustomersList: QueryList<StatisticsTotalCustomersComponent>;
    @ViewChildren('statisticsTotalAircraft')
    private statisticsTotalAircraftList: QueryList<StatisticsTotalAircraftComponent>;
    @ViewChildren('statisticsOrdersByLocation')
    private statisticsOrdersByLocationList: QueryList<StatisticsOrdersByLocationComponent>;

    widgets: DashboardWidget[] = [];

    //flghtWatch
    center: LngLatLike;
    flightWatchData: FlightWatchModelResponse[] = null;
    isStable: boolean = true;
    selectedICAO: string = "";
    isSingleSourceFbo: boolean = false;

    groupsFbosData: any = null;

    valueChangedSubscription: Subscription;
    changeEmittedSubscription: Subscription;
    isDefaultMap: boolean = true;

    integrationStatus: boolean = false;

    constructor(private sharedService: SharedService,
        private router: Router,
        private flightWatchMapService: FlightWatchMapService,
        private route: ActivatedRoute,
        private ls: StorageService,
    ) {
        // const queryParams = this.route.snapshot.queryParams;
        // const maptype = queryParams[MapQueryParams.MapType];
        // this.isDefaultMap = maptype == MapType.Aero ? false : true;

        this.fboid = this.sharedService.currentUser.fboId;
        this.groupid = this.sharedService.currentUser.groupId;
        this.selectedICAO = this.sharedService.getCurrentUserPropertyValue(localStorageAccessConstant.icao);
        this.integrationStatus = this.sharedService.currentUser.integrationStatus;

        this.filterStartDate = new Date(
            moment().add(-12, 'M').format('MM/DD/YYYY')
        );
        this.filterEndDate = new Date(moment().format('MM/DD/YYYY'));
        this.pastThirtyDaysStartDate = new Date(
            moment().add(-30, 'days').format('MM/DD/YYYY')
        );

        this.isSingleSourceFbo = JSON.parse(
            this.sharedService
                .getCurrentUserPropertyValue(
                    localStorageAccessConstant.isSingleSourceFbo
                )
        );
    }

    get isCsr() {
        return this.sharedService.isCsr;
    }

    get isMember() {
        return this.sharedService.currentUser.role === 4;
    }
    async ngOnInit() {
        this.loadWidgetOrder();
        await this.setFlightWatchMapCenter();

        this.valueChangedSubscription = this.sharedService.valueChanged$.subscribe((value: {event: string, data: FlightWatchModelResponse[]}) => {
            if(value.event === SharedEvents.flightWatchDataEvent){
                if(value.data){
                    this.flightWatchData = this.flightWatchMapService.filterArrivalsAndDepartures(value.data);
                    this.isStable = true;
                }else{
                    this.flightWatchData = null;
                    this.isStable = false;
                }
            }
        });
    }

    ngAfterViewInit() {
        this.locationChangedSubscription =
            this.changeEmittedSubscription = this.sharedService.changeEmitted$.subscribe((message) => {
                if (message === SharedEvents.locationChangedEvent) {
                    this.isSingleSourceFbo = JSON.parse(
                        this.sharedService
                            .getCurrentUserPropertyValue(
                                localStorageAccessConstant.isSingleSourceFbo
                            )
                    );
                    this.applyDateFilterChange();
                }else if(message == SharedEvents.icaoChangedEvent){
                    this.selectedICAO = this.sharedService.getCurrentUserPropertyValue(localStorageAccessConstant.icao);
                    this.setFlightWatchMapCenter();
                }
            });
    }

    ngOnDestroy() {
        this.changeEmittedSubscription?.unsubscribe();
        this.valueChangedSubscription?.unsubscribe();
        if (this.locationChangedSubscription) {
            this.locationChangedSubscription.unsubscribe();
        }
    }
    async setFlightWatchMapCenter(){
        if(this.selectedICAO && this.selectedICAO != undefined && this.selectedICAO != 'undefined'){
            this.center = await this.flightWatchMapService.getMapCenter(this.selectedICAO);
        }
    }
    applyDateFilterChange() {
        this.statisticsTotalOrdersList?.first?.refreshData();
        this.statisticsTotalCustomersList?.first?.refreshData();
        this.statisticsTotalAircraftList?.first?.refreshData();
        this.statisticsOrdersByLocationList?.first?.refreshData();
    }

    loadWidgetOrder(): void {
        const savedOrder = this.sharedService.dashboardSettings.widgetOrder;
        if (savedOrder && savedOrder.length) {
            this.widgets = savedOrder
                .map(id => DEFAULT_WIDGET_ORDER.find(w => w.id === id))
                .filter(w => !!w);
            // Append any new widgets not in saved order
            DEFAULT_WIDGET_ORDER.forEach(w => {
                if (!this.widgets.find(existing => existing.id === w.id)) {
                    this.widgets.push(w);
                }
            });
        } else {
            this.widgets = [...DEFAULT_WIDGET_ORDER];
        }
    }

    onWidgetDrop(event: CdkDragDrop<DashboardWidget[]>): void {
        moveItemInArray(this.widgets, event.previousIndex, event.currentIndex);
        this.sharedService.dashboardSettings.widgetOrder = this.widgets.map(w => w.id);
        this.ls.set(
            localStorageAccessConstant.dashboardSettings,
            JSON.stringify(this.sharedService.dashboardSettings)
        );
    }

    isWidgetVisible(widget: DashboardWidget): boolean {
        switch (widget.id) {
            case 'lost-opportunities':
            case 'new-fuelerlinx-operators':
            case 'flight-watch':
                return !this.integrationStatus;
            case 'lost-to-competition':
                return !this.integrationStatus && !this.isSingleSourceFbo;
            default:
                return true;
        }
    }

    gotoFlightWatch(){
        const uri  = '/default-layout/flight-watch';
        

        if(this.isDefaultMap){
            this.router.navigate([uri]);
        }else{
            this.router.navigate([uri], {
                queryParams: { 'map-type' : MapType.Aero }  
              });
        }
    }
}
